import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGameState, GAME_TYPES } from '../hooks/useGameState';
import PitchingTab from '../components/tracker/PitchingTab';
import BattingTab from '../components/tracker/BattingTab';
import FieldingTab from '../components/tracker/FieldingTab';
import api from '../services/api';
import '../components/tracker/Tracker.css';

// ─────────────────────────────────────────────
// TAB CONFIG
// ─────────────────────────────────────────────

const ALL_TABS = [
  { id: 'pitching', label: 'Pitching' },
  { id: 'batting', label: 'Batting' },
  { id: 'fielding', label: 'Fielding' },
];

function getVisibleTabs(isPitcher, isPitcherOnly) {
  if (!isPitcher) return ALL_TABS.filter((t) => t.id !== 'pitching');
  if (isPitcherOnly) return ALL_TABS.filter((t) => t.id === 'pitching');
  return ALL_TABS;
}

// ─────────────────────────────────────────────
// TRACKER PAGE
// ─────────────────────────────────────────────

export default function TrackerPage() {
  const game = useGameState();
  const { state } = game;

  const [activeTab, setActiveTab] = useState(() => {
    const tabs = getVisibleTabs(
      state.playerIsPitcher,
      state.playerIsPitcherOnly
    );
    return tabs[0].id;
  });
  const [players, setPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [setupDone, setSetupDone] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [showEndGame, setShowEndGame] = useState(false);
  const [showDecisionTooltip, setShowDecisionTooltip] = useState(false);
  const [showGearModal, setShowGearModal] = useState(false);
  const [showPitchingGear, setShowPitchingGear] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedPlayerId = location.state?.preselectedPlayerId || null;
  const resumeGameId = location.state?.resumeGameId || null;
  const [resumeAttempted, setResumeAttempted] = useState(false);
  const [editingSetup, setEditingSetup] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamSwitch, setShowTeamSwitch] = useState(false);

  // ── Scroll to top on mount and when game starts ───────────────
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (setupDone) {
      window.scrollTo(0, 0);
    }
  }, [setupDone]);
  // ── Load players on mount ─────────────────
  useEffect(() => {
    async function fetchPlayers() {
      try {
        const res = await api.get('/analytics/players');
        setPlayers(res.data || []);
      } catch (err) {
        console.error('Failed to load players:', err);
      } finally {
        setLoadingPlayers(false);
      }
    }
    fetchPlayers();
  }, []);

  // ── Restore setup state if game was in progress ───────────────
  useEffect(() => {
    if (state.playerId && state.opponent && state.gameDate) {
      setSetupDone(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Periodic background sync: create-or-update, debounced after activity ───
  const syncTimeoutRef = React.useRef(null);
  useEffect(() => {
    if (!setupDone) return;
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(async () => {
      try {
        const payload = {
          ...game.gamePayload,
          teamId: selectedTeam?.id || null,
          teamName: selectedTeam?.name || null,
          teamType: selectedTeam?.type || null,
          membership: selectedTeam?.membership || null,
          status: 'in_progress',
          liveState: state,
        };
        if (!state.gameId) {
          const res = await api.post(`/analytics/players/${state.playerId}/games`, payload);
          game.setGameInfo('gameId', res.data.id);
        } else {
          await api.put(`/analytics/players/${state.playerId}/games/${state.gameId}`, payload);
        }
      } catch (err) {
        console.error('Background game sync failed, will retry on next change:', err);
      }
    }, 4000);
    return () => clearTimeout(syncTimeoutRef.current);
  }, [state, setupDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-select single player + restore team on load ─────────
  useEffect(() => {
    const autoSelectId = preselectedPlayerId || (players.length === 1 ? players[0].id : null);
    if (autoSelectId && (!state.playerId || preselectedPlayerId !== state.playerId)) {
      const p = players.find((pl) => pl.id === autoSelectId) || (players.length === 1 ? players[0] : null);
      if (p) {
        game.setPlayer(
          p.id,
          `${p.firstName} ${p.lastName}`,
          p.isPitcher,
          p.isPitcherOnly,
          p.pitches
        );
        const tabs = getVisibleTabs(p.isPitcher, p.isPitcherOnly);
        setActiveTab(tabs[0].id);
        const ct =
          p.teams && p.teams.length > 0
            ? p.teams.find((t) => t.id === p.currentTeamId) || p.teams[0]
            : null;
        setSelectedTeam(ct);
      }
    } else if (state.playerId && !selectedTeam) {
      // Restore team if game was already in progress
      const player = players.find((p) => p.id === state.playerId);
      if (player && player.teams && player.teams.length > 0) {
        const ct =
          player.teams.find((t) => t.id === player.currentTeamId) ||
          player.teams[0];
        setSelectedTeam(ct);
      }
    }
  }, [players]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resume an in-progress game from another device ────────────
  useEffect(() => {
    if (!resumeGameId || resumeAttempted) return;
    if (!preselectedPlayerId || state.playerId !== preselectedPlayerId) return;
    setResumeAttempted(true);
    async function resumeGame() {
      try {
        const res = await api.get(`/analytics/players/${preselectedPlayerId}/games/${resumeGameId}`);
        if (res.data.liveState) {
          game.restoreLiveState(res.data.liveState);
        }
        setSetupDone(true);
      } catch (err) {
        console.error('Failed to resume game:', err);
      }
    }
    resumeGame();
  }, [preselectedPlayerId, resumeGameId, resumeAttempted, state.playerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ─────────────────────────────

  function handlePlayerSelect(player) {
    console.log('handlePlayerSelect called with:', player.id, player.firstName);
    console.log('state.playerId before:', state.playerId);
    game.setPlayer(
      player.id,
      `${player.firstName} ${player.lastName}`,
      player.isPitcher,
      player.isPitcherOnly,
      player.pitches
    );
    const tabs = getVisibleTabs(player.isPitcher, player.isPitcherOnly);
    setActiveTab(tabs[0].id);
    // Auto-set current team
    const ct =
      player.teams && player.teams.length > 0
        ? player.teams.find((t) => t.id === player.currentTeamId) ||
          player.teams[0]
        : null;
    setSelectedTeam(ct);
  }

  function getCurrentPlayerObj() {
    return players.find((p) => p.id === state.playerId) || null;
  }

  function handleSetupSubmit(e) {
    e.preventDefault();
    if (!state.playerId) return;
    if (!state.opponent.trim()) return;
    setSetupDone(true);
    setEditingSetup(false);
  }

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    setSaveStatus(null);
    try {
      const payload = {
        ...game.gamePayload,
        teamId: selectedTeam?.id || null,
        teamName: selectedTeam?.name || null,
        teamType: selectedTeam?.type || null,
        membership: selectedTeam?.membership || null,
        status: 'completed',
      };
      if (state.gameId) {
        await api.put(`/analytics/players/${state.playerId}/games/${state.gameId}`, payload);
      } else {
        await api.post(`/analytics/players/${state.playerId}/games`, payload);
      }
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Save failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (state.gameId) {
      try {
        await api.delete(`/analytics/players/${state.playerId}/games/${state.gameId}`);
      } catch (err) {
        console.error('Failed to delete canceled game:', err);
      }
    }
    game.resetAll();
    setSetupDone(false);
    setShowReset(false);
    setSaveStatus(null);
    navigate('/analytics/players');
  }

  function resetAfterSave() {
    game.resetAll();
    setSetupDone(false);
    setSaveStatus(null);
    navigate('/analytics/players');
  }

  // ─────────────────────────────────────────
  // RENDER: SETUP SCREEN
  // ─────────────────────────────────────────

  if (!setupDone || editingSetup) {
    return (
      <div className="tracker-page">
        <div className="tracker-setup-card">
          <div className="tracker-setup-header">
            {!editingSetup && (
              <button
                type="button"
                onClick={() => navigate('/analytics/players')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-2)',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: '0 0 12px 0',
                  fontFamily: 'var(--font-ui)',
                }}
              >
                ← Back to Players
              </button>
            )}
            <h2>{editingSetup ? 'Edit Game Info' : 'New Game'}</h2>
            <p>
              {editingSetup
                ? 'Update your game details.'
                : 'Set up your game before you start tracking.'}
            </p>
          </div>

          <form onSubmit={handleSetupSubmit} className="tracker-setup-form">
            {/* Player picker */}
            <div className="setup-field">
              <label>Player *</label>
              {loadingPlayers ? (
                <div className="setup-loading">Loading players…</div>
              ) : players.length === 0 ? (
                <div className="setup-empty">
                  No players found.{' '}
                  <a href="/analytics/players/new">Add a player</a> first.
                </div>
              ) : players.length === 1 ? (
                // Single player — auto-selected, just show name
                <div className="setup-auto-player">
                  <div className="setup-auto-player-name">
                    {players[0].firstName} {players[0].lastName}
                  </div>
                  <div className="setup-auto-player-meta">
                    {players[0].sport}
                  </div>
                </div>
              ) : (
                // Multiple players — card picker
                <div className="setup-player-cards">
                  {players.map((p) => {
                    const ct =
                      p.teams && p.teams.length > 0
                        ? p.teams.find((t) => t.id === p.currentTeamId) ||
                          p.teams[0]
                        : null;
                    const isSelected = state.playerId === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className={`setup-player-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => handlePlayerSelect(p)}
                      >
                        <div className="setup-player-card-name">
                          {p.firstName} {p.lastName}
                          {p.isPitcher && (
                            <span className="player-role-badge">
                              {p.isPitcherOnly ? 'PO' : 'P'}
                            </span>
                          )}
                        </div>
                        <div className="setup-player-card-meta">
                          <span className="player-sport-badge">{p.sport}</span>
                          {ct && (
                            <span className="setup-player-card-team">
                              {ct.name}
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <span className="setup-player-card-check">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <a
                href="/analytics/players"
                className="setup-manage-players-link"
              >
                Manage players →
              </a>
            </div>

            {/* Team selector — shown after player is picked */}
            {state.playerId &&
              (() => {
                const player = getCurrentPlayerObj();
                if (!player || !player.teams || player.teams.length === 0)
                  return null;
                return (
                  <div className="setup-field">
                    <label>Playing for</label>
                    <div className="setup-team-selector">
                      <div className="setup-team-selected">
                        <div className="setup-team-selected-info">
                          <span className="setup-team-selected-name">
                            {selectedTeam?.name || 'No team selected'}
                          </span>
                          {selectedTeam && (
                            <span
                              className={`team-membership-badge ${selectedTeam.membership}`}
                            >
                              {selectedTeam.membership === 'full_time'
                                ? 'Full Time'
                                : 'Guest'}
                            </span>
                          )}
                        </div>
                        {player.teams.length > 1 && (
                          <button
                            type="button"
                            className="setup-team-change-btn"
                            onClick={() => setShowTeamSwitch(true)}
                          >
                            Change
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

            {/* Date */}
            <div className="setup-field">
              <label htmlFor="game-date">Date *</label>
              <input
                id="game-date"
                type="date"
                value={state.gameDate}
                onChange={(e) => game.setGameInfo('gameDate', e.target.value)}
                required
              />
            </div>

            {/* Opponent */}
            <div className="setup-field">
              <label htmlFor="opponent">Opponent *</label>
              <input
                id="opponent"
                type="text"
                placeholder=""
                value={state.opponent}
                onChange={(e) => game.setGameInfo('opponent', e.target.value)}
                required
              />
            </div>

            {/* Home / Away */}
            <div className="setup-field">
              <label>Home or Away?</label>
              <div className="setup-toggle-row">
                {['home', 'away'].map((val) => (
                  <button
                    key={val}
                    type="button"
                    className={`setup-toggle-btn ${state.homeAway === val ? 'active' : ''}`}
                    onClick={() => game.setGameInfo('homeAway', val)}
                  >
                    {val === 'home' ? '🏠 Home' : '✈️ Away'}
                  </button>
                ))}
              </div>
            </div>

            {/* Game Type */}
            <div className="setup-field">
              <label htmlFor="game-type">Game Type</label>
              <select
                id="game-type"
                value={state.gameType}
                onChange={(e) => game.setGameInfo('gameType', e.target.value)}
              >
                {GAME_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* Venue */}
            <div className="setup-field">
              <label htmlFor="venue">
                Venue <span className="optional">(optional)</span>
              </label>
              <input
                id="venue"
                type="text"
                placeholder=""
                value={state.venue}
                onChange={(e) => game.setGameInfo('venue', e.target.value)}
              />
            </div>

            {/* Notes */}
            <div className="setup-field">
              <label htmlFor="notes">
                Notes <span className="optional">(optional)</span>
              </label>
              <textarea
                id="notes"
                rows={2}
                placeholder="e.g. First start after injury, tournament opener…"
                value={state.notes}
                onChange={(e) => game.setGameInfo('notes', e.target.value)}
              />
            </div>

            {editingSetup && (
              <button
                type="button"
                className="reset-btn"
                style={{ marginBottom: '8px' }}
                onClick={() => setEditingSetup(false)}
              >
                ← Back to Tracker
              </button>
            )}
            <button
              type="submit"
              className="setup-submit-btn"
              disabled={!state.playerId || !state.opponent.trim()}
            >
              {editingSetup ? 'Update Game Info' : 'Start Tracking →'}
            </button>
          </form>

          {/* Team switch modal for setup screen */}
          {showTeamSwitch &&
            (() => {
              const player = getCurrentPlayerObj();
              if (!player) return null;
              return (
                <div
                  className="modal-overlay"
                  onClick={() => setShowTeamSwitch(false)}
                >
                  <div
                    className="team-switch-sheet"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="team-switch-title">Switch Team</div>
                    <div className="team-switch-subtitle">
                      {player.firstName} {player.lastName}
                    </div>
                    <div className="team-switch-list">
                      {player.teams.map((team) => {
                        const isCurrent = selectedTeam?.id === team.id;
                        return (
                          <button
                            key={team.id}
                            className={`team-switch-option ${isCurrent ? 'current' : ''}`}
                            onClick={() => {
                              setSelectedTeam(team);
                              setShowTeamSwitch(false);
                            }}
                          >
                            <div className="team-switch-option-info">
                              <div className="team-switch-option-name">
                                {team.name}
                              </div>
                              <div className="team-switch-option-meta">
                                {team.type}
                                {team.season ? ` · ${team.season}` : ''}
                              </div>
                            </div>
                            <div className="team-switch-option-right">
                              <span
                                className={`team-membership-badge ${team.membership}`}
                              >
                                {team.membership === 'full_time'
                                  ? 'Full Time'
                                  : 'Guest'}
                              </span>
                              {isCurrent && (
                                <span className="team-switch-current-check">
                                  ✓ Current
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <button
                      className="team-switch-cancel"
                      onClick={() => setShowTeamSwitch(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })()}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────
  // RENDER: ACTIVE TRACKER
  // ─────────────────────────────────────────

  return (
    <div className="tracker-page">
      {/* ── Gear Modal ───────────────────── */}
      {showGearModal && (
        <div className="modal-overlay" onClick={() => setShowGearModal(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>

            {/* Game Info */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 8 }}>Game Info</div>
              <button
                className="tracker-game-meta tracker-meta-edit"
                style={{ width: '100%', textAlign: 'left', background: 'var(--surface-2)', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)' }}
                onClick={() => { setEditingSetup(true); setShowGearModal(false); }}
              >
                <span>{state.gameDate ? new Date(state.gameDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}</span>
                <span className="meta-dot">·</span>
                <span>vs {state.opponent}</span>
                <span className="meta-dot">·</span>
                <span className={`home-away-badge ${state.homeAway}`}>
                  {state.homeAway === 'home' ? '🏠 Home' : '✈️ Away'}
                </span>
                <span className="meta-dot">·</span>
                <span className="game-type-badge">{state.gameType}</span>
                <span className="meta-edit-hint">✎</span>
              </button>
              {selectedTeam && (
                <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-2)' }}>
                  {selectedTeam.name}
                </div>
              )}
            </div>

            {/* Save Game */}
            <button
              className="end-game-btn"
              style={{ width: '100%', marginBottom: 10 }}
              onClick={() => { setShowGearModal(false); setShowEndGame(true); }}
            >
              Game Over
            </button>
            <button
              className="new-game-link"
              style={{ width: '100%', textAlign: 'center', display: 'block' }}
              onClick={() => { setShowGearModal(false); setShowReset(true); }}
            >
              Cancel Game
            </button>

            <div style={{ marginTop: 16 }}>
              <button className="fld-modal-cancel" style={{ width: '100%' }} onClick={() => setShowGearModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header bar ───────────────────── */}
      <div className="tracker-header">
        <button
          className="tracker-back-btn"
          onClick={() => navigate('/analytics/players')}
          title="Back to players"
        >
          ←
        </button>
        <button
          className="tracker-gear-btn"
          onClick={() => setShowGearModal(true)}
          title="Settings"
        >
          ⚙️ Settings
        </button>
        <div className="tracker-player-name">{state.playerName}</div>
      </div>

      {/* ── Tab nav ──────────────────────── */}
      <div className="tracker-tab-nav" style={{ display: 'flex', alignItems: 'center' }}>
        {getVisibleTabs(state.playerIsPitcher, state.playerIsPitcherOnly).map(
          (tab) => (
            <button
              key={tab.id}
              className={`tracker-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          )
        )}
      </div>

      {/* ── Tab panels ───────────────────── */}
      <div className="tracker-tab-content">
        <div style={{ display: activeTab === 'pitching' ? 'block' : 'none' }}>
          <PitchingTab game={game} showGear={showPitchingGear} onGearClose={() => setShowPitchingGear(false)} onGearOpen={() => setShowPitchingGear(true)} />
        </div>
        <div style={{ display: activeTab === 'batting' ? 'block' : 'none' }}>
          <BattingTab game={game} />
        </div>
        <div style={{ display: activeTab === 'fielding' ? 'block' : 'none' }}>
          <FieldingTab game={game} pitching={state.pitching} />
        </div>
      </div>

      {/* ── End Game modal ───────────────── */}
      {showEndGame && (
        <div className="modal-overlay" onClick={() => setShowEndGame(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Save this game?</h3>
            <p>
              Want to save this game to {state.playerName}'s profile? Your stats
              will be stored and show up in their analytics.
            </p>
            <p className="modal-reset-warning">
              ⚠️ This will reset all stats and start a fresh game.
            </p>

            <div className="modal-score-section">
              <div className="modal-score-label">
                Final Score <span className="optional">(optional)</span>
              </div>
              <div className="score-inputs">
                <input
                  type="number"
                  min="0"
                  max="99"
                  placeholder="Us"
                  value={state.teamScore ?? ''}
                  onChange={(e) =>
                    game.setGameInfo(
                      'teamScore',
                      e.target.value === '' ? null : parseInt(e.target.value)
                    )
                  }
                  className="score-input"
                  aria-label="Team score"
                />
                <span className="score-dash">–</span>
                <input
                  type="number"
                  min="0"
                  max="99"
                  placeholder="Them"
                  value={state.opponentScore ?? ''}
                  onChange={(e) =>
                    game.setGameInfo(
                      'opponentScore',
                      e.target.value === '' ? null : parseInt(e.target.value)
                    )
                  }
                  className="score-input"
                  aria-label="Opponent score"
                />
              </div>
            </div>

            {state.pitching.actions.length > 0 && (
              <div className="modal-score-section">
                <div className="modal-score-label">
                  Pitcher Decision <span className="optional">(optional)</span>
                  <button
                    className="modal-tooltip-btn"
                    onClick={() => setShowDecisionTooltip(v => !v)}
                    type="button"
                  >?</button>
                </div>
                {showDecisionTooltip && (
                  <div className="modal-tooltip-box">
                    <strong>W</strong> — Win: your pitcher was the pitcher of record when your team took the lead for good.<br/>
                    <strong>L</strong> — Loss: your pitcher gave up the lead that your team never regained.<br/>
                    <strong>S</strong> — Save: came in relief, preserved the lead, finished the game (specific rules apply).<br/>
                    <strong>H</strong> — Hold: came in relief, kept the lead, but didn't finish the game.<br/>
                    <strong>BS</strong> — Blown Save: came in to protect a lead but gave it up.<br/>
                    <strong>ND</strong> — No Decision: none of the above apply — most common for starters and middle relievers.<br/>
                    <em>Not sure? Leave it blank or select ND.</em>
                  </div>
                )}
                <div className="pt-decision-row" style={{ marginTop: 8 }}>
                  {['W', 'L', 'S', 'H', 'BS', 'ND'].map((d) => (
                    <button
                      key={d}
                      className={`pt-decision-btn ${state.pitching.decision === d ? 'active' : ''}`}
                      onClick={() => game.setPitchingDecision(state.pitching.decision === d ? null : d)}
                      type="button"
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="modal-actions" style={{ flexDirection: 'column' }}>
              <button
                className={`save-btn save-btn-large ${saveStatus || ''}`}
                style={{}}
                onClick={async () => {
                  await handleSave();
                  setShowEndGame(false);
                  resetAfterSave();
                }}
                disabled={saving}
              >
                {saving
                  ? 'Ending Game…'
                  : saveStatus === 'success'
                    ? '✓ Game Over!'
                    : 'Game Over'}
              </button>
              <button
                className="btn-secondary"
                style={{ width: '100%' }}
                onClick={() => setShowEndGame(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Team switch modal ────────────── */}
      {showTeamSwitch &&
        (() => {
          const player = getCurrentPlayerObj();
          if (!player) return null;
          return (
            <div
              className="modal-overlay"
              onClick={() => setShowTeamSwitch(false)}
            >
              <div
                className="team-switch-sheet"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="team-switch-title">Switch Team</div>
                <div className="team-switch-subtitle">{state.playerName}</div>
                <div className="team-switch-list">
                  {player.teams.map((team) => {
                    const isCurrent = selectedTeam?.id === team.id;
                    return (
                      <button
                        key={team.id}
                        className={`team-switch-option ${isCurrent ? 'current' : ''}`}
                        onClick={() => {
                          setSelectedTeam(team);
                          setShowTeamSwitch(false);
                        }}
                      >
                        <div className="team-switch-option-info">
                          <div className="team-switch-option-name">
                            {team.name}
                          </div>
                          <div className="team-switch-option-meta">
                            {team.type}
                            {team.season ? ` · ${team.season}` : ''}
                          </div>
                        </div>
                        <div className="team-switch-option-right">
                          <span
                            className={`team-membership-badge ${team.membership}`}
                          >
                            {team.membership === 'full_time'
                              ? 'Full Time'
                              : 'Guest'}
                          </span>
                          {isCurrent && (
                            <span className="team-switch-current-check">
                              ✓ Current
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  className="team-switch-cancel"
                  onClick={() => setShowTeamSwitch(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          );
        })()}

      {/* ── Reset confirmation modal ──────── */}
      {showReset && (
        <div className="modal-overlay" onClick={() => setShowReset(false)}>
          <div
            className="modal-card reset-confirm-card"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Cancel Game?</h3>
            <p>
              This will clear all tracking data and return you to your players list.
            </p>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowReset(false)}
              >
                Cancel
              </button>
              <button className="btn-danger" onClick={handleReset}>
                Yes, Cancel Game
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
